import React from 'react'
import { useState, useEffect, startTransition } from 'react'
import { defaultProfileBannerUrl, isDefaultProfileBannerPath, resolveProfileBannerUrl } from '../../constants/DefaultAssets'
import useMakeRequest from '../../utils/ApiHelper'
import { ApiConstants } from '../../constants/ApiConstants'
import { toast } from 'react-toastify'
import { imageExtentionSupport } from '../../utils/Helper'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage } from '@hookform/error-message';
import profileValidation from '../../Validation/Profilevaalidation'
import { ApiTypes } from '../../constants/Constants'
import { ProfileAction } from '../../redux/Action/Authaction'
import { useDispatch } from 'react-redux'
import CommanPhoneNumber from '../layout/CommanPhoneNumber'
import BrandedLoaderOverlay from '../layout/BrandedLoaderOverlay'
import { useResourceEverLoaded } from '../layout/useSmartBrandedLoader'
import '../layout/page-loading-shell.scss'
import InlineLoadingButton from '../layout/InlineLoadingButton'
import { LanguageConstants } from '../../constants/LanguageConstants'
import { Modal } from 'react-bootstrap'
import VerfiyOtpEmail from '../../Modal/VerfiyOtpEmail'

type ProfileAccountType = 'business' | 'individual' | 'legacy';

interface FormInputs {
  profile_account_type: ProfileAccountType;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  phone_country_code: string;
  business_field_type: string;
  contact_name: string;
  company_tax_id: string;
  company_address: string;
  additional_info: string;
}

function splitProfileName(fullName: string) {
  const normalizedName = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalizedName) {
    return { first_name: '', last_name: '' };
  }
  const nameParts = normalizedName.split(' ');
  return {
    first_name: nameParts[0] || '',
    last_name: nameParts.slice(1).join(' '),
  };
}

function deriveProfileAccountType(d: any): ProfileAccountType {
  if (d?.account_type === 'business' || d?.account_type === 'individual') {
    return d.account_type;
  }
  if (d?.first_name || d?.last_name) {
    return 'individual';
  }
  return 'legacy';
}

function readBusinessField(d: any, businessDetails: any, key: string, ...fallbackKeys: string[]) {
  const order = [key, ...fallbackKeys];
  for (const k of order) {
    const raw = d?.[k] ?? businessDetails?.[k];
    if (raw != null && String(raw).trim() !== '') return String(raw).trim();
  }
  return '';
}

function Profile() {
  const profile = useMakeRequest()
  const VerifyEmail = useMakeRequest()
  const profileUpdate = useMakeRequest()
  const [image, setImage] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [showimage, setShowImage] = useState('')
  const [bannerPreview, setBannerPreview] = useState(defaultProfileBannerUrl())
  const dispatch = useDispatch()
  /** After OTP success while email still differs from last saved profile until Profile refreshes. */
  const [emailChangeVerified, setEmailChangeVerified] = useState(false)
  const [otpShow, setOtpShow] = useState(false)
  const [initialData, setInitialData] = useState<any>({});
  const [isChanged, setIsChanged] = useState(false);
  /** Post-update profile refetch — keep form visible; no full-page loader. */
  const [isSilentProfileRefresh, setIsSilentProfileRefresh] = useState(false);


  const { register, watch, setValue, trigger, formState: { errors }, handleSubmit } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      profile_account_type: 'legacy',
      name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      phone_country_code: '',
      business_field_type: '',
      contact_name: '',
      company_tax_id: '',
      company_address: '',
      additional_info: '',
    },
    resolver: yupResolver(profileValidation),
  });

  const profileAccountType = watch('profile_account_type');
  const wName = watch('name');
  const wFirstName = watch('first_name');
  const wLastName = watch('last_name');
  const wEmail = watch('email');
  const wPhone = watch('phone_number');
  const wPhoneCode = watch('phone_country_code');
  const wBusinessFieldType = watch('business_field_type');
  const wContactName = watch('contact_name');
  const wCompanyTaxId = watch('company_tax_id');
  const wCompanyAddress = watch('company_address');
  const wAdditionalInfo = watch('additional_info');
  const profileInfo = profile?.data?.data || {};
  const isSocialBusinessProfile =
    profileAccountType === 'business' && Boolean(profileInfo?.social_id);

  useEffect(() => {
    setEmailChangeVerified(false);
  }, [wEmail]);

  const savedProfileEmail = profile?.data?.data?.email ?? '';
  const emailDiffersFromSaved =
    String(wEmail || '').trim() !== String(savedProfileEmail || '').trim();
  const emailHasValidationError = Boolean(errors.email);
  const needsEmailOtp =
    emailDiffersFromSaved && !emailHasValidationError && !emailChangeVerified;

  const isSavingProfile = profileUpdate.loading;
  const profileEverLoaded = useResourceEverLoaded(profile?.data?.data != null);
  const initialProfilePending =
    !profileEverLoaded && profile.error == null && !profile.status;
  const showProfileLoader =
    isSavingProfile ||
    VerifyEmail?.loading ||
    initialProfilePending ||
    (profile.loading && !isSilentProfileRefresh && !profileEverLoaded);
  const showPageContent = profileEverLoaded || profile.error != null;

  useEffect(() => {
    profile.makeRequest({ url: ApiConstants?.Profile, toastStatus: false });
  }, []);

  useEffect(() => {
    if (!profile.loading) {
      setIsSilentProfileRefresh(false);
    }
  }, [profile.loading]);
  const onSubmit = (data: FormInputs) => {
    if (isSavingProfile) return;
    if (needsEmailOtp) {
      toast.error(LanguageConstants?.VERFIY_EMAIL)
    } else {
      const base: Record<string, unknown> = {
        email: isSocialBusinessProfile
          ? String(wEmail || profileInfo?.email || '').trim()
          : (data.email || '').trim(),
        phone_number: data.phone_number,
        phone_country_code: data.phone_country_code,
      };
      if (data.profile_account_type === 'business' || data.profile_account_type === 'individual') {
        base.account_type = data.profile_account_type;
      }
      if (data.profile_account_type === 'business') {
        base.company_name = (data.name || '').trim();
        base.business_field_type = (data.business_field_type || '').trim();
        base.contact_name = (data.contact_name || '').trim();
        base.company_tax_id = (data.company_tax_id || '').trim();
        base.company_address = (data.company_address || '').trim();
        base.additional_info = (data.additional_info || '').trim();
      } else {
        base.first_name = (data.first_name || '').trim();
        base.last_name = (data.last_name || '').trim();
      }
      if (image) {
        base.profile_pic = image;
      }
      if (bannerImage) {
        base.banner_image = bannerImage;
      }
      profileUpdate.makeRequest({
        payload: base,
        type: ApiTypes.POSTAPIFORMDATA,
        url: ApiConstants?.UpdateProfile,
        toastStatus: true,
      })
    }
  }
  useEffect(() => {
    if (!profileUpdate.status) return;
    setImage('');
    setBannerImage('');
    setIsChanged(false);
    setIsSilentProfileRefresh(true);
    profile.makeRequest({ url: ApiConstants?.Profile, toastStatus: false });
    //@ts-ignore
    dispatch(ProfileAction());
  }, [profileUpdate.status]);

  useEffect(() => {
    if (!profile?.status) return;
    startTransition(() => {
      const d = profile?.data?.data;
      const derived = deriveProfileAccountType(d);
      const businessDetails = d?.business_details || d?.businessDetails || {};
      setValue('profile_account_type', derived);
      setValue('email', d?.email);
      setValue('phone_number', d?.phone_number);
      setValue('phone_country_code', d?.phone_country_code);
      setImage('');
      setBannerImage('');
      setShowImage(d?.profile_pic);
      setBannerPreview(resolveProfileBannerUrl(d?.banner_image));

      if (derived === 'business') {
        setValue('name', (d?.name || '').trim());
        setValue(
          'business_field_type',
          readBusinessField(d, businessDetails, 'business_field_type', 'business_type', 'businessType'),
        );
        setValue('contact_name', readBusinessField(d, businessDetails, 'contact_name', 'contactName'));
        setValue(
          'company_tax_id',
          readBusinessField(d, businessDetails, 'company_tax_id', 'companyTaxId', 'vat_number', 'vatNumber'),
        );
        setValue(
          'company_address',
          readBusinessField(d, businessDetails, 'company_address', 'companyAddress', 'address', 'business_address'),
        );
        setValue('additional_info', readBusinessField(d, businessDetails, 'additional_info', 'additionalInfo'));
      } else {
        const splitName = d?.first_name || d?.last_name
          ? {
              first_name: (d?.first_name || '').trim(),
              last_name: (d?.last_name || '').trim(),
            }
          : splitProfileName((d?.name || '').trim());
        setValue('first_name', splitName.first_name);
        setValue('last_name', splitName.last_name);
        setValue('name', '');
        setValue('business_field_type', '');
        setValue('contact_name', '');
        setValue('company_tax_id', '');
        setValue('company_address', '');
        setValue('additional_info', '');
      }

      setInitialData({
        profile_account_type: derived,
        name: derived === 'business' ? (d?.name || '').trim() : '',
        first_name: d?.first_name || (derived !== 'business' ? splitProfileName((d?.name || '').trim()).first_name : ''),
        last_name: d?.last_name || (derived !== 'business' ? splitProfileName((d?.name || '').trim()).last_name : ''),
        email: d?.email || '',
        phone_number: d?.phone_number || '',
        phone_country_code: d?.phone_country_code || '',
        profile_pic: d?.profile_pic || '',
        banner_image: resolveProfileBannerUrl(d?.banner_image),
        business_field_type:
          derived === 'business'
            ? readBusinessField(d, businessDetails, 'business_field_type', 'business_type', 'businessType')
            : '',
        contact_name: derived === 'business' ? readBusinessField(d, businessDetails, 'contact_name', 'contactName') : '',
        company_tax_id:
          derived === 'business'
            ? readBusinessField(d, businessDetails, 'company_tax_id', 'companyTaxId', 'vat_number', 'vatNumber')
            : '',
        company_address:
          derived === 'business'
            ? readBusinessField(d, businessDetails, 'company_address', 'companyAddress', 'address', 'business_address')
            : '',
        additional_info:
          derived === 'business'
            ? readBusinessField(d, businessDetails, 'additional_info', 'additionalInfo')
            : '',
      });
    });
  }, [profile.status])

  const selectFileHandler = (e: any) => {
    const getextention = e?.target?.files[0]?.name.split('.').pop()
    if (imageExtentionSupport?.includes(getextention)) {
      e.preventDefault();
      let files;
      if (e.dataTransfer) {
        files = e.dataTransfer.files;
      } else if (e.target) {
        files = e.target.files;
      }
      setImage(files[0])
      const reader = new FileReader();
      reader.onload = () => {
        setShowImage(reader.result as any);
      };
      reader.readAsDataURL(files[0]);
    } else {
      e.preventDefault();
      toast.error("Please upload valid image");
      return;
    }
  };

  const selectBannerFileHandler = (e: any) => {
    const getextention = e?.target?.files[0]?.name.split('.').pop()
    if (imageExtentionSupport?.includes(getextention)) {
      e.preventDefault();
      let files;
      if (e.dataTransfer) {
        files = e.dataTransfer.files;
      } else if (e.target) {
        files = e.target.files;
      }
      setBannerImage(files[0])
      const reader = new FileReader();
      reader.onload = () => {
        setBannerPreview(reader.result as any);
      };
      reader.readAsDataURL(files[0]);
    } else {
      e.preventDefault();
      toast.error("Please upload valid image");
      return;
    }
  };


  const handlePhoneNumber = (dialCode: string, nationalNumber: string) => {
    const d = String(dialCode ?? '').replace(/\D/g, '');
    setValue('phone_country_code', d ? `+${d}` : '');
    setValue('phone_number', nationalNumber ?? '');
  };

  const SendVerfiyOtp = async () => {
    const ok = await trigger('email');
    if (!ok) return;
    const email = watch('email');
    if (email) {
      const payload = {
        email,
      };
      VerifyEmail.makeRequest({ payload, type: ApiTypes.POST, url: ApiConstants?.VerifyEmail, toastStatus: true });
    } else {
      toast.error(LanguageConstants?.EMAIL_ADDRESS);
    }
  };
  useEffect(() => {
    if (VerifyEmail.status) {
      setOtpShow(true)
    }
  }, [VerifyEmail.status])


    // Check if form changed
  useEffect(() => {
    if (!initialData || !Object.keys(initialData).length) return;
    const at = profileAccountType;
    const currentData: Record<string, string | File | undefined> = {
      email: wEmail || '',
      phone_number: wPhone || '',
      phone_country_code: wPhoneCode || '',
      profile_pic: (image || showimage || '') as string | File,
      banner_image: (bannerImage || bannerPreview || defaultProfileBannerUrl()) as string | File,
      name: (wName || '').trim(),
      first_name: (wFirstName || '').trim(),
      last_name: (wLastName || '').trim(),
    };
    const keys = ['email', 'phone_number', 'phone_country_code', 'profile_pic', 'banner_image'] as const;
    let changed = keys.some((key) => initialData[key] !== currentData[key]);
    changed = changed || initialData.name !== currentData.name;
    changed = changed || initialData.first_name !== currentData.first_name;
    changed = changed || initialData.last_name !== currentData.last_name;
    if (at === 'business') {
      currentData.business_field_type = (wBusinessFieldType || '').trim();
      currentData.contact_name = (wContactName || '').trim();
      currentData.company_tax_id = (wCompanyTaxId || '').trim();
      currentData.company_address = (wCompanyAddress || '').trim();
      currentData.additional_info = (wAdditionalInfo || '').trim();
      changed = changed || initialData.business_field_type !== currentData.business_field_type;
      changed = changed || initialData.contact_name !== currentData.contact_name;
      changed = changed || initialData.company_tax_id !== currentData.company_tax_id;
      changed = changed || initialData.company_address !== currentData.company_address;
      changed = changed || (initialData.additional_info || '') !== (currentData.additional_info || '');
    }
    setIsChanged(changed);
  }, [
    profileAccountType,
    wName,
    wFirstName,
    wLastName,
    wEmail,
    wPhone,
    wPhoneCode,
    wBusinessFieldType,
    wContactName,
    wCompanyTaxId,
    wCompanyAddress,
    wAdditionalInfo,
    image,
    bannerImage,
    showimage,
    bannerPreview,
    initialData,
  ]);

  const showingDefaultBanner =
    !bannerImage &&
    (bannerPreview === defaultProfileBannerUrl() || isDefaultProfileBannerPath(bannerPreview));

  return (
    <>
      <BrandedLoaderOverlay active={showProfileLoader} immediate />
      {showPageContent ? (
      <main className="page-loading-shell__content">
        <div className="container">
          <div className="profile-area page-upper-space mx-auto">
            <div className="profile-card">
              <section
                className={`profile-banner${showingDefaultBanner ? ' profile-banner--placeholder' : ''}`}
                style={
                  showingDefaultBanner
                    ? undefined
                    : { background: `url(${bannerPreview}) center/cover` }
                }
              >
                <input className="d-none" id="chooseProfileBanner" type="file" onChange={(e) => selectBannerFileHandler(e)} />
                {showingDefaultBanner ? (
                  <label className="profile-banner__placeholder" htmlFor="chooseProfileBanner">
                    <i className="icon-upload" aria-hidden="true" />
                    <span>{LanguageConstants?.UPLOAD_BANNER_IMAGE ?? 'Upload Banner Image'}</span>
                  </label>
                ) : null}
                <div className="profile-banner__actions">
                  <label
                    className="profile-banner__edit"
                    htmlFor="chooseProfileBanner"
                    aria-label={LanguageConstants?.UPDATE_BANNER_IMAGE ?? 'Update banner image'}
                    title={LanguageConstants?.UPDATE_BANNER_IMAGE ?? 'Update banner image'}
                  >
                    <i className="icon-edit"></i>
                  </label>
                </div>
              </section>
              <div className="profile-card__body">
                <h2 className="text-capitalize sec-ttl mb-5">{LanguageConstants?.MY_DETAILS ?? 'My Details'}</h2>
                <form onSubmit={handleSubmit(onSubmit)} >
                  <input type="hidden" {...register('profile_account_type')} />
                  <div className={`profile-img-row px-sm-4 ${profileAccountType === 'business' ? ' profile-img-row--business' : ''}`}>
                    <input className="d-none" id="chooseProfileImage" type="file" onChange={(e) => selectFileHandler(e)} />
                    <div className="profile-image-block">
                      <label className="pro-img-show" htmlFor="chooseProfileImage">
                        {showimage ?
                          <img src={showimage} alt='profile' />
                          : null}
                        {!showimage ?
                          <i className="icon-upload profile-upload-icon" />
                          : null}
                      </label>
                      <label className="profile-edit-trigger" htmlFor="chooseProfileImage">
                        <i className="icon-edit"></i>
                        <span>{LanguageConstants?.EDIT_PHOTO ?? "Edit Photo"}</span>
                      </label>
                    </div>
                    <div className={`profile-form-column${profileAccountType === 'business' ? ' profile-form-column--business' : ''}`}>
                      {profileAccountType === 'business' ? (
                          <div className='row g-4'>
                            <div className="col-lg-6">
                              <div className="row gy-4">
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.COMPANY_NAME_LABEL ?? "Company Name"}</label>
                                      <input
                                        className="f-input"
                                        type="text"
                                        placeholder={LanguageConstants?.COMPANY_NAME_PLACEHOLDER ?? "Company name"}
                                        autoComplete="organization"
                                        {...register('name')}
                                      />
                                      <ErrorMessage
                                        errors={errors}
                                        name="name"
                                        render={({ message }) => <p className="input-error">{message}</p>}
                                      />
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.CONTACT_NAME ?? "Contact Name"}</label>
                                      <input
                                        className="f-input"
                                        type="text"
                                        placeholder={LanguageConstants?.CONTACT_NAME_PLACEHOLDER ?? "Contact name"}
                                        autoComplete="name"
                                        {...register('contact_name')}
                                      />
                                      <ErrorMessage
                                        errors={errors}
                                        name="contact_name"
                                        render={({ message }) => <p className="input-error">{message}</p>}
                                      />
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.COMPANY_EMAIL ?? "Company Email"}</label>
                                      <input
                                        className="f-input"
                                        type="email"
                                        placeholder={`${LanguageConstants?.ENTER_EMAIL_ID}`}
                                        {...register('email')}
                                        disabled={isSocialBusinessProfile}
                                        readOnly={isSocialBusinessProfile}
                                        aria-readonly={isSocialBusinessProfile ? 'true' : undefined}
                                      />
                                      {needsEmailOtp && !isSocialBusinessProfile ?
                                        <i className='icon-tick-2 verify-input-icon' style={{ cursor: 'pointer' }} onClick={() => SendVerfiyOtp()} ></i>
                                        : null}
                                      <ErrorMessage
                                        errors={errors}
                                        name="email"
                                        render={({ message }) => <p className='input-error'>{message}</p>}
                                      />
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                  <div className="profile-form-field--phone">
                                    <div className='wrap-phn-code wrap-phn-code--integrated'>
                                      <CommanPhoneNumber
                                        hideLabel={false}
                                        handleMobileNumber={handlePhoneNumber}
                                        value={watch('phone_country_code')}
                                        nationalNumber={watch('phone_number')}
                                        splitInput={false}
                                      />
                                      <input id="business_profile_phone" type="hidden" {...register('phone_number')} />
                                      <input type="hidden" {...register('phone_country_code')} />
                                      <ErrorMessage
                                        errors={errors}
                                        name="phone_number"
                                        render={({ message }) => <p className='input-error'>{message}</p>}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="row gy-4">
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.BUSINESS_FIELD_TYPE ?? "Business Field Type"}</label>
                                      <input
                                        className="f-input"
                                        type="text"
                                        placeholder={LanguageConstants?.BUSINESS_FIELD_TYPE_PLACEHOLDER ?? "Business field type"}
                                        autoComplete="organization-title"
                                        {...register('business_field_type')}
                                      />
                                      <ErrorMessage
                                        errors={errors}
                                        name="business_field_type"
                                        render={({ message }) => <p className="input-error">{message}</p>}
                                      />
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.COMPANY_TAX_ID_VAT_NUMBER ?? "Company Tax ID / VAT Number"}</label>
                                      <input
                                        className="f-input"
                                        type="text"
                                        placeholder={LanguageConstants?.COMPANY_TAX_ID_VAT_NUMBER_PLACEHOLDER ?? "Company tax id / VAT number"}
                                        autoComplete="off"
                                        {...register('company_tax_id')}
                                      />
                                      <ErrorMessage
                                        errors={errors}
                                        name="company_tax_id"
                                        render={({ message }) => <p className="input-error">{message}</p>}
                                      />
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.COMPANY_ADDRESS_LABEL ?? "Company Address"}</label>
                                      <input
                                        className="f-input"
                                        type="text"
                                        placeholder={LanguageConstants?.COMPANY_ADDRESS_PLACEHOLDER ?? "Company address"}
                                        autoComplete="street-address"
                                        {...register('company_address')}
                                      />
                                      <ErrorMessage
                                        errors={errors}
                                        name="company_address"
                                        render={({ message }) => <p className="input-error">{message}</p>}
                                      />
                                    </div>
                                </div>
                                <div className="col-lg-12">
                                    <div className="com-floating not-have-icon mb-0">
                                      <label className="f-label"> {LanguageConstants?.ADDITIONAL_INFO_LABEL ?? "Additional Info"}</label>
                                      <input
                                        className="f-input"
                                        type="text"
                                        placeholder={LanguageConstants?.OPTIONAL_TEXT ?? "Optional"}
                                        {...register('additional_info')}
                                      />
                                      <ErrorMessage
                                        errors={errors}
                                        name="additional_info"
                                        render={({ message }) => <p className="input-error">{message}</p>}
                                      />
                                    </div>
                                </div>
                              </div>
                            </div>
                          </div>
                      ) : (
                        <div className="row gy-3">
                          <div className="col-lg-12">
                            <div className="com-floating not-have-icon mb-0">
                              <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.FIRST_NAME ?? "First Name"}</label>
                              <input
                                className="f-input"
                                type="text"
                                placeholder={LanguageConstants?.FIRST_NAME ?? "First Name"}
                                autoComplete="given-name"
                                {...register('first_name')}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="first_name"
                                render={({ message }) => <p className="input-error">{message}</p>}
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="com-floating not-have-icon mb-0">
                              <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.LAST_NAME ?? "Last Name"}</label>
                              <input
                                className="f-input"
                                type="text"
                                placeholder={LanguageConstants?.LAST_NAME ?? "Last Name"}
                                autoComplete="family-name"
                                {...register('last_name')}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="last_name"
                                render={({ message }) => <p className="input-error">{message}</p>}
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="com-floating not-have-icon mb-0">
                              <label className="f-label"><span className="must-star">*</span> {LanguageConstants?.EMAIL_ID}</label>
                              <input className="f-input" type="email" placeholder={`${LanguageConstants?.ENTER_EMAIL_ID}`}  {...register('email')} disabled={profileInfo?.social_id ? true : false} />

                              {needsEmailOtp ?
                                <i className='icon-tick-2 verify-input-icon' style={{ cursor: 'pointer' }} onClick={() => SendVerfiyOtp()} ></i>
                                : null}
                              <ErrorMessage
                                errors={errors}
                                name="email"
                                render={({ message }) => <p className='input-error'>{message}</p>}
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="profile-form-field--phone">
                              <div className='wrap-phn-code wrap-phn-code--integrated'>
                                <CommanPhoneNumber
                                  hideLabel={false}
                                  handleMobileNumber={handlePhoneNumber}
                                  value={watch('phone_country_code')}
                                  nationalNumber={watch('phone_number')}
                                  splitInput={false}
                                />
                                <input id="profile_phone_number" type="hidden" {...register('phone_number')} />
                                <input type="hidden" {...register('phone_country_code')} />
                              </div>
                              <ErrorMessage
                                errors={errors}
                                name="phone_number"
                                render={({ message }) => <p className='input-error'>{message}</p>}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {(isChanged || isSavingProfile) ? (
                        <div className="profile-form-actions mt-5">
                          <InlineLoadingButton
                            type="submit"
                            className="round-btn w-100"
                            loading={isSavingProfile}
                          >
                            {LanguageConstants?.UPDATE}
                          </InlineLoadingButton>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      ) : null}

      <Modal show={otpShow} >

        <VerfiyOtpEmail
          show={otpShow}
          setOtpShow={setOtpShow}
          email={watch('email')}
          onEmailVerified={() => setEmailChangeVerified(true)}
        />

      </Modal>

    </>
  )
}

export default Profile
